import type { ParsedUrlQuery } from 'node:querystring'

import { HorizonFlyDatePicker } from '@pcln/horizon-fly-date-picker'
import type { FlightFormState } from '@pcln/horizon-search-form'
import { FlightSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useMemo, useState } from 'react'

import { DEFAULT_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import { fireInfantErrorEvent } from '@/components/TravelerSelection/ga4'
import { useFlightAirportNesting } from '@/components/TypeAhead/useFlightAirportNesting'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'

import { ONE_WAY, ROUND_TRIP } from '../constants'
import { fireBundleAndSaveEvent } from '../ga4'
import type { FlightFormStateType } from '../types'
import { DEFAULT_VALUES } from './constants'
import useInitialFlightFormValues from './useInitialFlightFormValues'

const EXPRESS_DEAL_FLIGHT_TYPES = [ROUND_TRIP, ONE_WAY] as const

type FlightSearchFormProps = {
  isRebook?: boolean
  isModify?: boolean
  onSubmit: (pageName: GA4PageNameType, data: FlightFormStateType, queryParams?: ParsedUrlQuery) => void
  pageName?: GA4PageNameType
  isExpressDeal?: boolean
}

export default function HorizonFlightForm({
  isRebook = false,
  isModify = false,
  onSubmit,
  pageName = 'homepage',
  isExpressDeal = false,
}: FlightSearchFormProps) {
  const { initialValues, query, isMobile, cguid, appName, appVersion } = useInitialFlightFormValues(
    isRebook,
    isModify,
  )

  // WEB_MWEB_FLIGHT_SEARCH_AIRPORT_NESTING — every field on this form uses the flights endpoint,
  // and the hook applies its own homepage gate.
  const airportNestingEnabled = useFlightAirportNesting('flights')

  const [trackedAirports, setTrackedAirports] = useState({
    startId: initialValues.flights?.[0]?.startLocation?.id ?? '',
    startCityCode: initialValues.flights?.[0]?.startLocation?.cityCode ?? '',
    endId: initialValues.flights?.[0]?.endLocation?.id ?? '',
    endCityCode: initialValues.flights?.[0]?.endLocation?.cityCode ?? '',
  })

  const memoizedFlights = useMemo(
    () => [
      {
        departureAirport: { id: trackedAirports.startId, cityCode: trackedAirports.startCityCode },
        arrivalAirport: { id: trackedAirports.endId, cityCode: trackedAirports.endCityCode },
      },
    ],
    [
      trackedAirports.startId,
      trackedAirports.startCityCode,
      trackedAirports.endId,
      trackedAirports.endCityCode,
    ],
  )

  const handleAutocompleteSelect = useCallback(
    (searchKey: string, item: { id: string; cityCode?: string } | null) => {
      if (searchKey === 'flights.0.startLocation') {
        setTrackedAirports((prev) => ({
          ...prev,
          startId: item?.id ?? '',
          startCityCode: item?.cityCode ?? '',
        }))
      } else if (searchKey === 'flights.0.endLocation') {
        setTrackedAirports((prev) => ({
          ...prev,
          endId: item?.id ?? '',
          endCityCode: item?.cityCode ?? '',
        }))
      }
    },
    [],
  )

  const removeBackground =
    useSeti({
      experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP',
      shouldFireImpression: !isExpressDeal,
    }) === 'VARIANT'

  const isRoundTripFlight = (initialValues.flightType ?? DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP
  const isPackageCheckboxHighlightEnabled =
    useSeti({
      experimentCode: 'PKG_MWEB_CHECKBOX_HIGHLIGHT',
      shouldFireImpression: isMobile && isRoundTripFlight && !isExpressDeal,
    }) === 'VARIANT' && isMobile

  const apolloClientInfo = useMemo(
    () => ({ clientName: appName, clientVersion: appVersion }),
    [appName, appVersion],
  )

  return (
    <div className='flex w-auto flex-col gap-3'>
      <FlightSearchForm
        initialValues={initialValues as unknown as Partial<FlightFormState>}
        isMobile={isMobile}
        autoAdvance={!isMobile}
        removeBackground={isExpressDeal || removeBackground}
        showBundleAndSave={!isExpressDeal}
        showCabinClass={!isExpressDeal}
        flightTypes={isExpressDeal ? EXPRESS_DEAL_FLIGHT_TYPES : undefined}
        highlightBundleAndSave={isPackageCheckboxHighlightEnabled}
        isTravelerSelectionDisabled={isRebook || isModify}
        onInfantError={() => fireInfantErrorEvent(pageName, 'air')}
        onBundleChange={(tripType, checked) => {
          if (checked) {
            try {
              fireBundleAndSaveEvent(tripType)
            } catch {
              // GA4TripTypeMapper may throw on unexpected trip type combinations
            }
          }
        }}
        onSubmit={(data) => {
          if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
            window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT')
            window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT_DATA', {
              originCityName: data.flights[0]?.startLocation?.cityName,
              originAirportCode: data.flights[0]?.startLocation?.id,
              destinationCityName: data.flights[0]?.endLocation?.cityName,
              destinationCityAirportCode: data.flights[0]?.endLocation?.id,
              departureDate: data.flights[0]?.startDate,
              returnDate: data.flights[0]?.endDate,
            })
          }
          onSubmit(pageName, data as FlightFormStateType, query)
          logSearchFormAnalytics({ cguid, pageName, productType: 'flights', isMobile })
        }}
        autocompleteOptions={{ isMobile, airportNestingEnabled }}
        onLocationSelect={(searchKey, item) => {
          handleAutocompleteSelect(searchKey, item)
        }}
        renderDatePicker={(props) => (
          <HorizonFlyDatePicker
            key={props.flightType}
            className='flight-dates'
            title={props.title}
            label={props.label}
            mode={props.mode}
            numberOfMonths={props.numberOfMonths}
            selected={props.selected}
            errorMessage={props.errorMessage}
            handleCalendarChange={props.handleCalendarChange}
            inputRef={props.inputRef}
            flightDetails={{
              cabinClass: props.cabinClass,
              tripType: props.flightType || DEFAULT_VALUES.FLIGHT_TYPE,
              flights: memoizedFlights,
            }}
            apolloClientInfo={apolloClientInfo}
            disabled={props.disabled}
            startMonth={props.startMonth}
            endMonth={props.endMonth}
            defaultMonth={props.defaultMonth}
            min={props.min}
            collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
          />
        )}
      />
    </div>
  )
}
